import React from "react";
import {
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { KeyboardArrowDown } from "@mui/icons-material";
import { GOAL_OPTIONS, MARKETPLACE_OPTIONS } from "types/onboarding";
import themes from "config/themes";

interface CompanyNameQuestionProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  showError: boolean;
}

export const CompanyNameQuestion: React.FC<CompanyNameQuestionProps> = ({
  value,
  onChange,
  error,
  showError,
}) => {
  return (
    <TextField
      name="companyName"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Enter your company name"
      fullWidth
      required
      error={showError}
      helperText={showError ? error : ""}
      sx={{
        maxWidth: 500,
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: themes.palette.customColor5.main,
          },
          "&:hover fieldset": {
            borderColor: themes.palette.customColor6.main,
          },
        },
      }}
    />
  );
};

interface GoalsQuestionProps {
  selectedGoals: string[];
  onChange: (goals: string[]) => void;
  error?: string;
  showError: boolean;
}

export const GoalsQuestion: React.FC<GoalsQuestionProps> = ({
  selectedGoals,
  onChange,
  error,
  showError,
}) => {
  const handleGoalToggle = (goal: string, checked: boolean) => {
    const nextGoals = checked
      ? [...selectedGoals, goal]
      : selectedGoals.filter((g) => g !== goal);
    onChange(nextGoals);
  };

  return (
    <FormControl error={showError} component="fieldset" sx={{ maxWidth: 500 }}>
      <FormGroup>
        {GOAL_OPTIONS.map((goal) => (
          <FormControlLabel
            key={goal}
            control={
              <Checkbox
                checked={selectedGoals.includes(goal)}
                onChange={(e) => handleGoalToggle(goal, e.target.checked)}
                sx={{
                  color: themes.palette.tertiary.main,
                  "&.Mui-checked": {
                    color: themes.palette.info.main,
                  },
                }}
              />
            }
            label={goal}
            sx={{ alignItems: "center" }}
          />
        ))}
      </FormGroup>
      {showError && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

interface SelectQuestionProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  error?: string;
  showError: boolean;
}

export const SelectQuestion: React.FC<SelectQuestionProps> = ({
  label,
  value,
  options,
  onChange,
  error,
  showError,
}) => {
  return (
    <FormControl fullWidth sx={{ maxWidth: 500 }} error={showError}>
      <InputLabel sx={{ color: themes.palette.tertiary.main }}>
        {label}
      </InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(e: SelectChangeEvent) => onChange(e.target.value)}
        IconComponent={KeyboardArrowDown}
        required
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: themes.palette.customColor5.main,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: themes.palette.customColor6.main,
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
      {showError && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

interface MarketplaceQuestionProps {
  marketplace: string;
  customMarketplace: string;
  onMarketplaceChange: (value: string) => void;
  onCustomMarketplaceChange: (value: string) => void;
  marketplaceError?: string;
  customMarketplaceError?: string;
  showMarketplaceError: boolean;
  showCustomMarketplaceError: boolean;
}

export const MarketplaceQuestion: React.FC<MarketplaceQuestionProps> = ({
  marketplace,
  customMarketplace,
  onMarketplaceChange,
  onCustomMarketplaceChange,
  marketplaceError,
  customMarketplaceError,
  showMarketplaceError,
  showCustomMarketplaceError,
}) => {
  return (
    <>
      <SelectQuestion
        label="Select a marketplace"
        value={marketplace}
        options={MARKETPLACE_OPTIONS}
        onChange={onMarketplaceChange}
        error={marketplaceError}
        showError={showMarketplaceError}
      />

      {marketplace === "Other" && (
        <TextField
          name="customMarketplace"
          value={customMarketplace}
          onChange={(e) => onCustomMarketplaceChange(e.target.value)}
          placeholder="Please specify the marketplace"
          fullWidth
          required
          error={showCustomMarketplaceError}
          helperText={showCustomMarketplaceError ? customMarketplaceError : ""}
          sx={{
            mt: 2,
            maxWidth: 500,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: themes.palette.customColor5.main,
              },
              "&:hover fieldset": {
                borderColor: themes.palette.customColor6.main,
              },
            },
          }}
        />
      )}
    </>
  );
};
